<template>
  <div style="flex-grow: 1; display: flex; align-items: center; justify-content: space-around;">
    <Button severity="secondary" class="big-button" @click="showDialog('save')">
      Save to file
    </Button>
    <Button severity="secondary" class="big-button" @click="showDialog('load')">
      Load from file
    </Button>

    <Dialog v-model:visible="showingDialog" modal dismissableMask :header="(dialogType == 'load') ? 'Load settings' : 'Save settings'">
      <div class="dialogContent" style="display: flex; flex-direction: column; gap: 10px; font-size: large;">
        <div v-if="dialogType == 'load'" style="display: flex; flex-wrap: wrap; align-items: center; gap: 10px;">
          <Button severity="secondary" :class="buttonClass('userSaved')" @click="selectFileType('userSaved')">
            Named files
          </Button>
          <Button severity="secondary" :class="buttonClass('autoSaved')" @click="selectFileType('autoSaved')">
            Autosave files
          </Button>
          <template v-if="store.showAdvanced">
            <Button severity="secondary" :class="buttonClass('defaults')" @click="selectFileType('defaults')">
              System defaults
            </Button>
          </template>
        </div>
        <div style="display: flex; gap: 10px; flex-grow: 1; flex-wrap: wrap;">
          <div class="file-list" v-if="fileType != 'defaults'">
            <div v-if="filesPending || filteredFiles.length == 0" style="padding: 10px;">
              {{ filesPending ? 'Loading' : 'No files to show' }}
            </div>
            <table v-else>
              <tr v-for="file in filteredFiles" :class="fileClass(file)">
                <th style="white-space: nowrap; text-align: left;" @click="selectFile(file)">
                  {{ file.name }}
                </th>
                <td style="width: 100%; font-style: italic; font-size: small;" @click="selectFile(file)">
                  {{ timestamp(file) }}
                </td>
                <td @click="deleteFile(file)">
                  <i v-if="fileType=='userSaved' || store.showAdvanced" class="pi pi-trash" style="margin-right: 10px;"></i>
                </td>
              </tr>
            </table>
          </div>
          <div class="group-list" v-if="store.showAdvanced && dialogType == 'load'" style="display: flex; flex-direction: column; gap: 5px;">
            <template v-for="(group, index) in groups" >
              <Checkbox v-model="selectedGroups" :value="group.code" :binary="false">
                <span style="white-space: nowrap;">
                  {{ group.label }}
                </span>
              </Checkbox>
            </template>
            <Checkbox v-model="allSelected">
              Select all
            </Checkbox>
          </div>
        </div>
        <div v-if="fileType != 'defaults'" style="margin-top: 10px; display: flex; gap: 10px; align-items: center;">
          File name:
          <InputText style="flex-grow: 1; padding-left: 10px;" :style="{ color: validFileName ? '' : 'red' }"
            v-model.trim="selectedFileName" 
            @keypress.enter="submit()"
          ></InputText>
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 10px;">
          <Button :disabled="!submitEnabled" @click="submit()">
            {{ dialogType == 'load' ? 'Load' : 'Save' }}
          </Button>
          <Button severity="secondary" :disabled="submitPending" @click="closeDialog()">
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import store from "./store.js";
import { padStart } from "./utility.js";

export default {
  created() {
    this.listFiles();
    store.sessions[0].queueRequest("/cmd/listUploadGroups", {}, (groups) => {
      this.groups = groups;
      this.selectDefautGroups();
    });
  },
  data() { 
    return {
      store,
      files: null,
      fileType: "userSaved",
      dialogType: null,
      groups: [],
      filesPending: false,
      submitPending: false,
      selectedGroups: [],
      selectedFileName: null,
    };
  },
  watch: {
    "store.showAdvanced": {
      handler() {
        this.selectDefautGroups();
        if (this.fileType == 'defaults')
          this.fileType = 'userSaved';
      }
    }
  },
  computed: {
    showingDialog: {
      get() { return this.dialogType != null; },
      set(value) { 
        console.assert(value == false);
        this.dialogType = null;
      }
    },
    filteredFiles() {
      if (!this.files) 
        return [];

      let files = [];
      if (this.fileType == "autoSaved")
        files = this.files.autoSaved;
      if (this.fileType == "userSaved")
        files = this.files.userSaved;

      files.sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));
      return files;
    },
    selectedFile: {
      get() { return this.filteredFiles.find(f => f.name == this.selectedFileName)},
      set(file) { this.selectedFileName = file?.name }
    },
    validFileName() {
      if (this.dialogType == 'load') {
        // Ensure a file with the given name has actually been selected.
        return this.selectedFile != null;
      }
      else {
        // Forbids saving a new file with the following chars =;:%"<>,*?|\/"
        return this.selectedFileName?.match(/^[^=;:%"<>\,\*\?\|\\\/]+$/g)?.length == 1;
      }
    },
    submitEnabled() {
      let enabled = !this.submitPending;

      if (this.dialogType == 'load')
        enabled &= (this.selectedGroups.length > 0);
      
      if (this.fileType != 'defaults') 
        enabled &= !this.filesPending && this.validFileName;

      return enabled;
    },
    allSelected: {
      get() {
        return this.selectedGroups.length == this.groups.length;
      },
      set(value) {
        if (value)
          this.selectedGroups = this.groups.map((group, index) => index);
        else
          this.selectedGroups = [];
      }
    }
  },
  methods: {
    showDialog(dialogType) {
      this.selectedFile = null;

      this.dialogType = dialogType;
      if (dialogType == 'save') 
        this.fileType = 'userSaved';

      this.listFiles();
    },
    closeDialog() {
      this.dialogType = null;
    },
    listFiles() {
      this.files = null;
      this.filesPending = true;
      store.sessions[0].queueRequest("/cmd/listFiles", {}, (files) => {
        this.filesPending = false;
        this.files = files;
      });
    },
    selectFileType(fileType) {
      this.fileType = fileType;
      this.selectedFile = ( this.filteredFiles.length > 0
        ? this.filteredFiles[0]
        : null
      );
    },
    selectFile(file) {
      this.selectedFile = file;
    },
    deleteFile(file) {
      if (confirm(`Delete file '${file.name}'?`)) {
        let filePath = `${this.fileType}/${file.name}.json`;
        store.sessions[0].queueRequest("/cmd/deleteFile", { filePath });
        this.selectedFileName = null;
        this.listFiles();
      }
    },
    selectDefautGroups() {
      this.selectedGroups = this.groups.filter(group => group.defaultChecked).map(group => group.code);
    },
    timestamp(file) {
      return formatTimestamp(file.modified);
    },
    submit() {
      if (!this.submitEnabled) {
        return;
      }
      this.submitPending = true;
      if (this.dialogType == 'load') {
        let fileName = (() => {
          switch (this.fileType) {
            case 'autoSaved': 
            case 'userSaved':
              return `${this.fileType}/${this.selectedFile.name}`;
            case 'defaults': 
              return 'defaults';
          }
        })();
        store.sessions[0].queueRequest("/cmd/loadFile", { fileName, groups: this.selectedGroups }, () => {
          this.submitPending = false;
          this.closeDialog();
          this.listFiles();
        });
      }
      else if (this.dialogType == 'save') {
        store.sessions[0].queueRequest("/cmd/saveFile", { fileName: this.selectedFileName }, () => {
          this.submitPending = false;
          this.closeDialog();
          this.listFiles();
        });
      }
    },
    buttonClass(fileType) {
      return {
        "file-type-button": true,
        selected: fileType == this.fileType
      }
    },
    fileClass(file) {
      return {
        "file-item": true,
        "selected": file == this.selectedFile
      };
    }
  }
}

function formatTimestamp(timestamp) {
  let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  let months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  let d = new Date(timestamp * 1000);
  
  let timePart = padStart(d.getHours(), 2, "0") + ":" + padStart(d.getMinutes(), 2, "0");
  let datePart = days[d.getDay()] + " " + months[d.getMonth()] + " " + d.getDate() + ", " + d.getFullYear();

  return timePart + " " + datePart;
}

</script>

<style scoped>
.big-button {
  height: 70%; 
  width: 40%;
  font-size: large;
}
.file-list {
  display: flex;
  flex-direction: column;
  border: 1px solid gray;
  flex-grow: 1;
  height: 328px;
  overflow: auto;
}
.desktop .dialogContent {
  min-width: 800px;
}
.file-list table {
  border-collapse: collapse;
}
.file-list th, .file-list td {
  padding: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-weight: normal;
}
.file-item {
  cursor: pointer;
  background: white;
}
.file-item.selected {
  background: rgb(172, 214, 255);
}
#app .border-btn {
  margin: 0;
}
.file-type-button {
  border: 1px solid gray;
  height: 35px;
  flex-grow: 1;
  font-size: medium;
}
.file-type-button.selected {
  background: #63b1ff61;
}
</style>